import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Wallet, RefreshCw, Loader2, CheckCircle2, Clock } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/pix`;

/**
 * Tela do admin para conferir depósitos PIX pendentes contra o extrato
 * bancário e confirmar manualmente - necessária quando a empresa opera
 * só com a chave PIX própria (sem gateway automático como Mercado Pago,
 * que credita sozinho via webhook).
 */
const DepositosPixAdmin = () => {
  const [carregando, setCarregando] = useState(true);
  const [pendentes, setPendentes] = useState([]);
  const [confirmando, setConfirmando] = useState(null);

  const carregar = async () => {
    setCarregando(true);
    try {
      const resp = await authService.fetchAuth(`${API}/admin/pendentes`);
      if (resp.ok) setPendentes(await resp.json());
    } catch (error) {
      console.error("Erro ao carregar depósitos pendentes:", error);
      toast.error("Não foi possível carregar os depósitos pendentes.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const confirmar = async (pagamento) => {
    if (!window.confirm(`Confirma que R$ ${pagamento.valor.toFixed(2)} de ${pagamento.email_pagador} caiu na conta da empresa de verdade?`)) return;
    setConfirmando(pagamento.id);
    try {
      const resp = await authService.fetchAuth(`${API}/confirmar-manualmente/${pagamento.id}`, {
        method: "POST",
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao confirmar depósito");
      toast.success("Depósito confirmado! Carteira do afiliado creditada.");
      await carregar();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setConfirmando(null);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Wallet className="w-7 h-7 text-emerald-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Depósitos PIX Pendentes</h1>
            <p className="text-sm text-gray-400">Confira contra o extrato da conta da empresa e confirme manualmente</p>
          </div>
        </div>
        <Button variant="outline" onClick={carregar}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {carregando ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando...
        </div>
      ) : pendentes.length === 0 ? (
        <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
          Nenhum depósito PIX pendente no momento.
        </Card>
      ) : (
        <div className="space-y-2">
          {pendentes.map((p) => (
            <Card key={p.id} className="p-4 bg-gray-800/50 border-gray-700 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">R$ {(p.valor || 0).toFixed(2)}</p>
                <p className="text-xs text-gray-400">{p.email_pagador} · {p.descricao}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                  <Clock className="w-3 h-3" /> Ref: {p.referencia_externa}
                </p>
              </div>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => confirmar(p)}
                disabled={confirmando === p.id}
              >
                {confirmando === p.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                Confirmar recebido
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DepositosPixAdmin;
